/**
 * Konkord: a game in a call as it travels between the players and as it is
 * saved (on the device and in the player's Saved Notes). Everything that
 * arrives from someone else goes through `decodeGame` before use.
 */
import {
  DIFFICULTIES,
  type Difficulty,
  type Minesweeper,
  type MinesweeperStatus,
} from "./minesweeper";

/**
 * free: everyone allowed clicks when they want; turns: one move each, in order
 */
export type GameMode = "free" | "turns";

export type GameRecord = {
  id: string;
  kind: "minesweeper";
  /** voice channel of the call */
  channel: string;
  /** who started it */
  owner: string;
  /** people the owner let play too */
  players: string[];
  /** whose app runs the game now (applies moves and sends the result) */
  host: string;
  /** grows with every takeover: a later host beats everything before it */
  epoch: number;
  /** grows with every change, newer wins (within an epoch) */
  seq: number;
  /** paused on purpose (the clock stops) */
  paused: boolean;
  /** board generation seed */
  seed: number;
  mode: GameMode;
  /** cells each player opened */
  scores: Record<string, number>;
  /** turns: whose move it is */
  turn?: string;
  /** turns: when that turn started (ms) */
  turnAt?: number;
  /** who opened the mine */
  explodedBy?: string;
  /** the finished game this one replays (everyone moves to the new one) */
  rematchOf?: string;
  /** last change (ms) */
  updatedAt: number;
  board: Minesweeper;
};

/** Saved Notes messages holding a game start with this */
export const SAVE_PREFIX = "konkord-game:";

const ID = /^[0-9A-HJKMNP-TV-Z]{26}$/;
const STATUSES: MinesweeperStatus[] = ["ready", "playing", "won", "lost"];
const MODES: GameMode[] = ["free", "turns"];
export const MAX_PLAYERS = 20;

export const canPlay = (game: GameRecord, user: string) =>
  game.owner === user || game.players.includes(user);

export function encodeGame(game: GameRecord) {
  return JSON.stringify({
    ...game,
    // one hex digit per cell keeps the biggest board far below Stoat's 2000
    // characters per message
    board: { ...game.board, cells: game.board.cells.map((c) => c.toString(16)).join("") },
  });
}

const finite = (value: unknown, min = 0): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= min;

/**
 * A game from the network or a save, undefined when anything is off
 */
export function decodeGame(text: string): GameRecord | undefined {
  let value: Record<string, unknown>;
  try {
    value = JSON.parse(text);
  } catch {
    return;
  }
  if (typeof value !== "object" || value === null) return;

  const { id, kind, channel, owner, players, host, epoch, seq, paused, seed, updatedAt } =
    value;
  // saves from before modes and scores: a free game with no scores
  const { mode = "free", scores = {}, turn, turnAt, explodedBy, rematchOf } = value;
  const optionalId = (v: unknown) => v === undefined || (typeof v === "string" && ID.test(v));
  if (
    !MODES.includes(mode as GameMode) ||
    typeof scores !== "object" ||
    scores === null ||
    Array.isArray(scores) ||
    Object.keys(scores).length > MAX_PLAYERS + 1 ||
    !Object.entries(scores).every(
      ([user, n]) => ID.test(user) && Number.isSafeInteger(n) && (n as number) >= 0,
    ) ||
    !optionalId(turn) ||
    (turnAt !== undefined && !finite(turnAt)) ||
    !optionalId(explodedBy) ||
    !optionalId(rematchOf)
  ) {
    return;
  }
  if (
    kind !== "minesweeper" ||
    typeof id !== "string" ||
    !ID.test(id) ||
    typeof channel !== "string" ||
    !ID.test(channel) ||
    typeof owner !== "string" ||
    !ID.test(owner) ||
    typeof host !== "string" ||
    !ID.test(host) ||
    !Array.isArray(players) ||
    players.length > MAX_PLAYERS ||
    !players.every((p) => typeof p === "string" && ID.test(p)) ||
    !Number.isSafeInteger(epoch) ||
    !finite(epoch) ||
    !Number.isSafeInteger(seq) ||
    !finite(seq) ||
    typeof paused !== "boolean" ||
    !Number.isInteger(seed) ||
    !finite(updatedAt)
  ) {
    return;
  }

  const board = value.board as Record<string, unknown> | null;
  if (typeof board !== "object" || board === null) return;
  const { difficulty, cells, status, exploded, elapsed, since } = board;
  if (typeof difficulty !== "string" || !(difficulty in DIFFICULTIES)) return;
  const size = DIFFICULTIES[difficulty as Difficulty];
  if (
    typeof cells !== "string" ||
    cells.length !== size.width * size.height ||
    !/^[0-9a-f]*$/.test(cells) ||
    !STATUSES.includes(status as MinesweeperStatus) ||
    !Number.isInteger(exploded) ||
    (exploded as number) < -1 ||
    (exploded as number) >= cells.length ||
    !finite(elapsed) ||
    (since !== undefined && !finite(since))
  ) {
    return;
  }

  return {
    id,
    kind,
    channel,
    owner,
    players: [...new Set(players as string[])],
    host,
    epoch: epoch as number,
    seq: seq as number,
    paused,
    seed: seed as number,
    mode: mode as GameMode,
    scores: { ...(scores as Record<string, number>) },
    turn: turn as string | undefined,
    turnAt: turnAt as number | undefined,
    explodedBy: explodedBy as string | undefined,
    rematchOf: rematchOf as string | undefined,
    updatedAt,
    board: {
      difficulty: difficulty as Difficulty,
      ...size,
      cells: [...cells].map((c) => parseInt(c, 16)),
      status: status as MinesweeperStatus,
      exploded: exploded as number,
      elapsed,
      since: since as number | undefined,
    },
  };
}
